/**
 * Техника (AK-235) — shared TypeScript types for the machinery catalog.
 *
 * Mirrors the backend Pydantic shapes in
 * `backend/src/ikolhoz/api/v1/reference.py` (machinery section). Variant C
 * polymorph: common columns + `attrs` JSONB whose shape is described by the
 * group's `fields_schema`. EN-перевод отложен (RU-only tab).
 */
import type { NameTranslations, PagedResponse } from './types';

export type { NameTranslations, PagedResponse };

/** JSON-Schema-lite field descriptor (same convention as operation_types). */
export interface MachineryFieldDef {
  key: string;
  type: 'number' | 'string' | 'enum' | 'bool' | 'date';
  label: string;
  unit?: string;
  options?: string[];
  help?: string;
  required?: boolean;
}

export const MACHINERY_GROUP_LABELS: Record<string, string> = {
  self_propelled: 'Самоходная',
  implement: 'Прицепные СХМ',
  aerial: 'Авиационная',
};

export function machineryGroupLabel(code: string): string {
  return MACHINERY_GROUP_LABELS[code] ?? code;
}

// ---------- Groups (fixed mini-reference + fields_schema) ----------

export interface MachineryGroup {
  id: string;
  code: string;
  name_translations: NameTranslations;
  fields_schema: MachineryFieldDef[];
  sort_order: number;
  is_active: boolean;
}

export interface MachineryGroupsEnvelope {
  items: MachineryGroup[];
}

// ---------- Types (FK group) ----------

export interface MachineryType {
  id: string;
  machinery_group: string;
  code: string;
  name_translations: NameTranslations;
  sort_order: number;
  is_active: boolean;
  usage_count: number;
}

export interface MachineryTypesEnvelope {
  items: MachineryType[];
}

export interface MachineryTypePayload {
  machinery_group: string;
  code: string;
  name_translations: NameTranslations;
  sort_order?: number;
  is_active?: boolean;
}

// ---------- Manufacturers ----------

export interface Manufacturer {
  id: string;
  code: string;
  name: string;
  country_code: string | null;
  is_active: boolean;
  usage_count: number;
}

export interface ManufacturersEnvelope {
  items: Manufacturer[];
}

export interface ManufacturerPayload {
  code: string;
  name: string;
  country_code?: string | null;
  is_active?: boolean;
}

// ---------- Models (main catalog) ----------

export interface MachineryModelListItem {
  id: string;
  /** Код марки в справочнике МДМ — стабильный ключ, по которому матчится книга нормативов. */
  code: string | null;
  name_translations: NameTranslations;
  /** Другие названия марки. Только выверенное — невыверенное живёт в очереди разбора. */
  also_known_as: string[];
  machinery_group: string;
  machinery_type_id: string | null;
  type_name: string | null;
  manufacturer_id: string | null;
  manufacturer_name: string | null;
  year_from: number | null;
  year_to: number | null;
}

export interface MachineryModelDetail extends MachineryModelListItem {
  attrs: Record<string, unknown>;
  compatible_model_ids: string[];
}

export interface MachineryModelPayload {
  code: string | null;
  name_translations: NameTranslations;
  also_known_as: string[];
  manufacturer_id: string | null;
  machinery_group: string;
  machinery_type_id: string | null;
  year_from: number | null;
  year_to: number | null;
  attrs: Record<string, unknown>;
}

export interface MachineryModelListQuery {
  search?: string;
  machinery_group?: string;
  machinery_type_id?: string;
  manufacturer_id?: string;
  limit?: number;
  offset?: number;
}

export interface CompatibilityPayload {
  compatible_model_ids: string[];
}
